import { useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import { router } from '@Routes/__root';
import { useBoundStore } from '@Store/index.ts';
import { routesModel } from '@Router/router.consts.ts';
import { RoutesMap } from '@Router/router.types.ts';

import { isAllowedPrev } from '@Hooks/custom/isAllowedPrev.service';
import type { NavigateOptions } from '@tanstack/react-router';

export const useNavigationManager = () => {
  const { getOnboardingData, currentPageIndex, editMode, setEditMode, setNavigationStack } =
    useBoundStore((state) => state.onboardingSlice);
  const search = useSearch({ strict: false });

  const isAllowedPrevPage = useMemo(
    () => isAllowedPrev({ editMode, currentPageIndex }),
    [currentPageIndex, editMode]
  );

  const navigateToFirstPage = (queryParams?: Record<string, unknown>) => {
    const { navigationStack } = getOnboardingData();
    const pageId = navigationStack[0];
    router.navigate({ to: routesModel[pageId].path, search: { ...search, ...queryParams } });
  };

  const _getNextPageIdByOrigin = (origin: string) => {
    const { navigationStack } = getOnboardingData();
    const navStackPageIndex = navigationStack.findIndex((page) => origin === page);
    const destinationIdx = navStackPageIndex + 1;
    if (destinationIdx >= navigationStack.length) {
      return navigationStack[navStackPageIndex];
    }
    return navigationStack[destinationIdx];
  };

  type Origin = RoutesMap | 'SKIP';

  const navigateToNextPage = (
    origin: Origin,
    queryParams?: NavigateOptions<typeof router>['search'] | null
  ) => {
    if (editMode) {
      router.navigate({ to: routesModel[RoutesMap.REVIEW].path });
      setEditMode(false);
    } else {
      let nextPageId = null;
      if (origin && origin !== 'SKIP') {
        nextPageId = _getNextPageIdByOrigin(origin);
      } else {
        const { navigationStack } = getOnboardingData();
        nextPageId = navigationStack[currentPageIndex + 1];
      }

      const navigationOptions = {
        to: routesModel[nextPageId].path,
      } as NavigateOptions<typeof router>;

      if (queryParams) {
        navigationOptions.search = queryParams;
      }
      router.navigate(navigationOptions);
    }
  };

  const navigateToPreviousPage = (queryParams?: Record<string, unknown>) => {
    const { navigationStack } = getOnboardingData();
    const previousPageId = navigationStack[currentPageIndex - 1];
    router.navigate({ to: routesModel[previousPageId].path, search: { ...queryParams } });
  };

  const navigateToSpecificPage = <TPageId extends RoutesMap>(
    pageId: TPageId,
    qParams?: NavigateOptions<typeof router>['search']
  ) => {
    const to = routesModel[pageId].path;
    const route = {
      to,
    } as NavigateOptions<typeof router>; //TODO: check if satisfies can work here
    if (qParams) {
      route.search = qParams;
    }
    router.navigate(route);
  };

  const removeNavStackItemByPageName = (pageName: RoutesMap) => {
    const { navigationStack } = getOnboardingData();
    const shallowCopy = [...navigationStack];
    const pageIdx = shallowCopy.indexOf(pageName);
    if (pageIdx === -1) {
      return;
    }
    shallowCopy.splice(pageIdx, 1);
    setNavigationStack(shallowCopy);
  };

  const addNavStackItemAfterSpecificPage = (basePage: RoutesMap, pageToAdd: RoutesMap) => {
    const { navigationStack } = getOnboardingData();
    const shallowCopy = [...navigationStack];
    const basePageIdx = shallowCopy.indexOf(basePage);
    const pageToAddIdx = shallowCopy.indexOf(pageToAdd);
    if (pageToAddIdx !== -1) {
      return;
    }
    shallowCopy.splice(basePageIdx + 1, 0, pageToAdd);
    setNavigationStack(shallowCopy);
  };

  // TODO: add function that gets a base page and a list of pages to add and adds them after the base page
  const addNavStackListItemsAfterSpecificPage = (basePage: RoutesMap, pagesToAdd: RoutesMap[]) => {
    const { navigationStack } = getOnboardingData();
    const shallowCopy = [...navigationStack];
    const basePageIdx = shallowCopy.indexOf(basePage);
    pagesToAdd.forEach((page) => {
      const pageIdx = shallowCopy.indexOf(page);
      if (pageIdx !== -1) {
        return;
      }
      shallowCopy.splice(basePageIdx + 1, 0, page);
    });
    setNavigationStack(shallowCopy);
  };
  return {
    navigateToFirstPage,
    navigateToNextPage,
    navigateToPreviousPage,
    isAllowedPrevPage,
    navigateToSpecificPage,
    removeNavStackItemByPageName,
    addNavStackItemAfterSpecificPage,
    addNavStackListItemsAfterSpecificPage,
  };
};
